import React from "react";

import { graphql } from "lib/apolloClient";
import _ from "lodash";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar";
import {
  Avatar,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import useI18n from "Hooks/UseI18n";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import Loading from "UIComponents/Loading";
import prepareLoadMoreFeed from "Utils/PrepareLoadMoreFeedHOF";
import { getOrganizationId } from "modules/CommonSelectors";

import { getStaffListQuery } from "MessagingV2/modules/MessagingQuery";
import { getFormattedNameForUserType } from "MessagingV2/modules/Utils";
import { GroupThreeFilled } from "@toddle-edu/ds-icons";

import classes from "./CreatorsFilter.scss";

const styles = {
  customAvatarStyle: {
    size: "xx-small",
  },
};

const CreatorFilter = props => {
  const {
    isLoading,
    staffEdges,
    hasNextPage,
    loadMoreStaff,
    setSearchText,
    selectedUsers,
    setSelectedUsers,
  } = props;
  const { t } = useI18n(["common"]);

  const options = React.useMemo(() => {
    return _.map(staffEdges, edge => {
      const user = _.get(edge, "node", {});
      const profileImage = _.get(user, "profileImage");
      const name = getFormattedNameForUserType({ user });
      return {
        value: user.id,
        label: name,
        prefixElement: (
          <MessagingAvatar
            id={user.id}
            name={name}
            url={profileImage}
            customAvatarStyles={styles.customAvatarStyle}
            customAvatarContainerClassName={classes.avatarContainer}
          />
        ),
      };
    });
  }, [staffEdges]);

  const onScrollEnd = () => {
    if (!isLoading && hasNextPage) {
      loadMoreStaff();
    }
  };

  return (
    <ChecklistDropdown
      options={options}
      showAllOption
      allOptionPrefix={
        <Avatar
          dsVersion="2.0"
          size="xx-small"
          icon={<GroupThreeFilled />}
          iconType="icon"
          shape="square"
        />
      }
      isSearchEnabled
      onSearchTextChange={setSearchText}
      value={selectedUsers}
      onChange={setSelectedUsers}
      showSelectedOptionsOnTop={false}
      dsVersion="2.0"
      onScrollEnd={onScrollEnd}
      loader={isLoading ? <Loading /> : null}
    >
      <FilterLabelButton
        dsVersion="2.0"
        size="medium"
        variant="neutral"
        type="outlined"
        label={t("common:creator")}
        labelValue={getLabelValueForMultiFilterLabelButton({
          selectedOptions: selectedUsers,
          options,
          t,
          noneText: t("common:all"),
        })}
      />
    </ChecklistDropdown>
  );
};

const CreatorFilterWithQuery = graphql(getStaffListQuery, {
  name: "getStaffList",
  options: ({ searchText }) => {
    return {
      fetchPolicy: "network-only",
      variables: {
        id: getOrganizationId(),
        first: 20,
        filters: {
          searchText,
        },
      },
    };
  },
  props({ getStaffList }) {
    const queryVariables = _.get(getStaffList, "variables", {});

    const node = _.get(getStaffList, "node", {});
    const pageInfo = _.get(node, "staff.pageInfo", {});
    const hasNextPage = _.get(node, "staff.pageInfo.hasNextPage", false);
    const staffEdges = _.get(node, "staff.edges", []);

    return {
      isLoading: _.includes([1, 2, 3], getStaffList["networkStatus"]),
      isData: !_.isEmpty(node),
      staffEdges,
      hasNextPage,
      loadMoreStaff: prepareLoadMoreFeed({
        variables: {
          ...queryVariables,
          after: _.get(pageInfo, "endCursor", undefined),
        },
        queryData: getStaffList,
        key: "staff",
      }),
    };
  },
})(CreatorFilter);

const CreatorFilterWithFilter = props => {
  const [searchText, setSearchText] = React.useState("");

  return (
    <CreatorFilterWithQuery
      searchText={searchText}
      setSearchText={setSearchText}
      {...props}
    />
  );
};

export default CreatorFilterWithFilter;
